import { Box, ButtonGroup, makeStyles, withStyles } from '@material-ui/core';
import { HeaderButton } from 'Components/Shared/ViewPageHeader/PageHeaderStyles';
import { useSearchParams } from 'react-router-dom';
import { Views } from 'Styles';
import { Grid as GridIcon, List as ListIcon } from 'Components/Shared/Icons';
import { Breadcrumb, Category, Customer } from 'Services/MongoRealm/Types';
import { TextColor } from '@setvi/shared/enums';
import { PriceGroup } from '@setvi/shared/interfaces';
import {
  ISearch,
  SBreadcrumbs,
  SButton,
  SText
} from '@setvi/shared/components';
import CloseIcon from '@material-ui/icons/Close';
import { CustomerDropdown } from 'componentsV2/customer-dropdown';
import { distances } from '@setvi/shared/styles';
import { SearchFormOptions } from '@setvi/shared/components/intelligence/product-match/enums';
import { SearchType } from '@setvi/shared/components/intelligence/search/interfaces';
import { options } from '@setvi/shared/components/intelligence/search';
import { AISearch } from 'pages/products/hooks/use-ai-search-result';
import { CustomerPreferences } from 'componentsV2';
import { CustomerPreferencesInterface } from 'componentsV2/customer-preferences/interfaces';
import { COMPANIES } from 'Enums/Companies';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { PriceGroupDropdown } from 'componentsV2/price-group-dropdown';

const useHeaderStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    padding: `${distances.px.base} ${distances.px.large}`
  }
});

export const Actions = withStyles({
  root: {
    marginLeft: 3,
    display: 'flex',
    gridGap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end'
  }
})(Box);

interface HeaderProps {
  searchQuery: AISearch;
  loading: boolean;
  selectedCategory: Category;
  actions?: JSX.Element;
  aiSearchType?: SearchFormOptions;
  aiSearchActive?: boolean;
  controller?: any;
  selectedCustomerPreference?: CustomerPreferencesInterface;
  setSelectedCustomerPreference?: (
    customerPreference: CustomerPreferencesInterface
  ) => void;
  aiEnabled?: boolean;
  filters?: {
    customer?: {
      show: boolean;
      value: Customer;
      onChange: (customer: Customer) => void;
    };
    priceGroup?: {
      show: boolean;
      value: PriceGroup;
      onChange: (priceGroup: PriceGroup) => void;
    };
  };
  onSelectCategory: (categoryID: Category['_id']) => void;
  handleSearch?: (val: {
    type: SearchType;
    search: AISearch;
    aiType?: SearchFormOptions;
  }) => void;
  refetchProducts: () => void;
  onSearchClear?: () => void;
  setSearchType?: (type: SearchType) => void;
}

export const Header = ({
  controller,
  searchQuery,
  loading,
  actions,
  selectedCategory,
  aiSearchType,
  aiSearchActive,
  selectedCustomerPreference,
  setSelectedCustomerPreference,
  handleSearch,
  filters = {},
  aiEnabled = true,
  onSelectCategory,
  refetchProducts,
  onSearchClear,
  setSearchType
}: HeaderProps) => {
  const { user } = useAppContext();
  const classes = useHeaderStyles();

  const [searchParams, setSearchParams] = useSearchParams();

  const allCategories = 'All Categories';

  const breadcrumbs = selectedCategory
    ? ([
        { _id: null as any, name: allCategories },
        ...selectedCategory.breadcrumbs
      ] as Breadcrumb[])
    : [];

  const onClose = () => {
    handleSearch({ type: SearchType.Regular, search: { value: '' } });
    setSearchType(SearchType.Regular);

    if (controller) {
      controller?.abort();
    }
  };

  return (
    <Box className={classes.container}>
      <Box
        display="flex"
        alignItems="start"
        flexDirection="column"
        justifyContent="center">
        {aiSearchActive ? (
          <SButton
            variant="outlined"
            style={{ borderRadius: 120 }}
            onClick={onClose}
            startIcon={<CloseIcon style={{ fontSize: 16 }} />}>
            Close Ask AI
          </SButton>
        ) : (
          <>
            <SText
              size="xl"
              title={selectedCategory ? selectedCategory.name : allCategories}
              weight="bold"
            />
            {!!breadcrumbs?.length && (
              <SBreadcrumbs<Breadcrumb>
                maxItems={2}
                parseBreadcrumb={breadcrumb => breadcrumb?.name}
                onBreadcrumbClick={breadcrumb =>
                  onSelectCategory(breadcrumb?._id)
                }
                items={breadcrumbs}
              />
            )}
          </>
        )}
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <ISearch
          aiEnabled={aiEnabled}
          onClear={onSearchClear}
          handleSearch={handleSearch}
          defaultOption={aiSearchActive ? options[1] : options[0]}
          searchQuery={searchQuery}
          aiSearchActive={aiSearchActive}
          type={aiSearchType}
          loading={loading}
        />
      </Box>
      <Actions>
        {!!actions && actions}

        {filters?.priceGroup?.show && (
          <PriceGroupDropdown
            newDesign
            style={{ dropdownWrapperWidth: 400 }}
            selectedPriceGroup={filters?.priceGroup?.value}
            onChange={filters?.priceGroup?.onChange}
          />
        )}
        {filters?.customer?.show && (
          <CustomerDropdown
            newDesign
            style={{ dropdownWrapperWidth: 400 }}
            selectedCustomer={filters?.customer.value}
            onSelectCustomer={filters?.customer?.onChange}
          />
        )}

        {!aiSearchActive && COMPANIES.ELANCO_PROD === user?.CompanyID && (
          <Box>
            <CustomerPreferences
              newDesign
              style={{ dropdownWrapperWidth: 458 }}
              selected={selectedCustomerPreference}
              onSelect={setSelectedCustomerPreference}
            />
          </Box>
        )}

        <ButtonGroup>
          <HeaderButton
            onClick={() => {
              refetchProducts();
              searchParams.set(
                'view',
                searchParams.get('view') === Views.GRID_VIEW
                  ? Views.LIST_VIEW
                  : Views.GRID_VIEW
              );
              setSearchParams(searchParams);
            }}>
            {searchParams.get('view') !== Views.GRID_VIEW ? (
              <GridIcon fill={TextColor.SlateGrey} />
            ) : (
              <ListIcon fill={TextColor.SlateGrey} />
            )}
          </HeaderButton>
        </ButtonGroup>
      </Actions>
    </Box>
  );
};
